import type { MotionAmplificationJobId } from "../clients/motion-amplification";
import {
  abortMotionAmplificationMultipartUpload,
  completeMotionAmplificationMultipartUpload,
  getMotionAmplificationUploadSessionStatus,
  initiateMotionAmplificationMultipartUpload,
  listMotionAmplificationMultipartParts,
  ModalityApiError,
  presignMotionAmplificationPart,
  uploadMotionAmplificationPart,
} from "../clients/motion-amplification";
import { S3UploadError } from "../clients/s3UploadError";
import {
  removeMultipartFileHandle,
  saveMultipartFileHandle,
} from "./multipartUploadFileHandleStore";
import {
  fingerprintFile,
  type MultipartUploadSession,
} from "./multipartUploadSession";

function clearSavedUpload(jobId: MotionAmplificationJobId) {
  void removeMultipartFileHandle(jobId).catch(() => undefined);
}

const MAX_CONCURRENT_PARTS = 3;
const MAX_PART_ATTEMPTS = 5;
const PART_TIMEOUT_MS = 4 * 60 * 1000;

function cancelledError() {
  return new Error("S3 upload was cancelled.");
}

export class MultipartUploadPausedError extends Error {
  constructor(
    readonly jobId: MotionAmplificationJobId,
    readonly session?: MultipartUploadSession,
  ) {
    super("Upload interrupted. Select the same video and retry to resume.");
    this.name = "MultipartUploadPausedError";
  }
}

async function getResumeStatus(
  jobId: MotionAmplificationJobId,
  sessionId: string,
) {
  try {
    return await getMotionAmplificationUploadSessionStatus(jobId, sessionId);
  } catch (error) {
    if (error instanceof ModalityApiError && error.statusCode === 404) {
      return null;
    }
    if (isRetryableUploadError(error))
      throw new MultipartUploadPausedError(jobId);
    throw error;
  }
}

function unavailableSessionError(message: string | null | undefined) {
  return new ModalityApiError(
    message ||
      "This upload session is no longer available. Upload the video again.",
    409,
  );
}

/** Whether an S3 or API upload failure is transient enough to retry. */
export function isRetryableUploadError(error: unknown): boolean {
  if (error instanceof S3UploadError) {
    if (error.kind === "network" || error.kind === "timeout") return true;
    if (error.kind !== "http") return false;
    return (
      error.httpStatus === 408 ||
      error.httpStatus === 429 ||
      (error.httpStatus !== undefined && error.httpStatus >= 500) ||
      (error.httpStatus === 403 &&
        (error.s3Code === "ExpiredToken" || error.s3Code === "RequestExpired"))
    );
  }
  if (error instanceof ModalityApiError) {
    return (
      error.statusCode === 408 ||
      error.statusCode === 429 ||
      error.statusCode >= 500
    );
  }
  return error instanceof TypeError;
}

/** Waits a jittered exponential backoff, rejecting early if the upload is cancelled. */
export function waitBeforeRetry(
  attempt: number,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(cancelledError());
    const timer = setTimeout(
      () => {
        signal.removeEventListener("abort", onAbort);
        resolve();
      },
      500 + Math.random() * Math.min(8000, 1000 * 2 ** attempt),
    );
    function onAbort() {
      clearTimeout(timer);
      reject(cancelledError());
    }
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export async function uploadMultipartMotionAmplificationVideo(
  jobId: MotionAmplificationJobId,
  file: File,
  onProgress: (progress: number) => void,
  signal: AbortSignal,
  savedSession?: MultipartUploadSession,
  fileHandle?: FileSystemFileHandle,
  onUploadStarted?: () => void,
): Promise<void> {
  const controller = new AbortController();
  const cancel = () => controller.abort(signal.reason);
  signal.addEventListener("abort", cancel, { once: true });
  if (signal.aborted) controller.abort(signal.reason);
  let sessionId: string | null = null;
  let completed = false;
  let workers: Promise<void>[] = [];
  let session: MultipartUploadSession | null = savedSession ?? null;
  let resuming = Boolean(session);
  let uploadStartedNotified = false;

  try {
    if (controller.signal.aborted) throw cancelledError();
    if (session && session.jobId !== jobId)
      throw new Error("Multipart job mismatch.");
    const fingerprint = await fingerprintFile(file);
    if (session && session.fingerprint !== fingerprint) {
      throw new Error(
        "The selected file does not match the interrupted upload.",
      );
    }
    if (session && session.fileSize !== file.size) {
      throw new Error("The selected file size does not match the upload.");
    }

    if (session) {
      const resumedSession = session;
      sessionId = resumedSession.sessionId;
      const status = await getResumeStatus(jobId, resumedSession.sessionId);
      if (status === null) {
        clearSavedUpload(jobId);
        session = null;
        sessionId = null;
        throw unavailableSessionError(null);
      }

      if (status.status === "completed") {
        clearSavedUpload(jobId);
        session = null;
        sessionId = null;
        completed = true;
        onProgress(100);
        return;
      }

      if (status.status === "completing") {
        const { parts: remoteParts } =
          await listMotionAmplificationMultipartParts(
            jobId,
            resumedSession.sessionId,
          );
        const partCount = Math.ceil(file.size / resumedSession.partSize);
        const savedParts = remoteParts
          .filter((part) => part.partNumber <= partCount)
          .map(({ partNumber, eTag }) => ({ partNumber, eTag }));
        if (savedParts.length !== partCount) {
          throw new MultipartUploadPausedError(jobId, resumedSession);
        }

        try {
          await completeMotionAmplificationMultipartUpload(
            jobId,
            resumedSession.sessionId,
            savedParts,
          );
          clearSavedUpload(jobId);
          session = null;
          sessionId = null;
          completed = true;
          onProgress(100);
          return;
        } catch (completionError) {
          const latestStatus = await getResumeStatus(
            jobId,
            resumedSession.sessionId,
          );
          if (latestStatus === null) {
            clearSavedUpload(jobId);
            session = null;
            sessionId = null;
            throw unavailableSessionError(null);
          }
          if (latestStatus.status === "completed") {
            clearSavedUpload(jobId);
            session = null;
            sessionId = null;
            completed = true;
            onProgress(100);
            return;
          }
          if (latestStatus.status === "completing") {
            throw new MultipartUploadPausedError(jobId, resumedSession);
          }
          if (latestStatus.status !== "uploading") {
            clearSavedUpload(jobId);
            session = null;
            sessionId = null;
            throw unavailableSessionError(latestStatus.errorMessage);
          }
          if (!isRetryableUploadError(completionError)) throw completionError;
          resuming = true;
        }
      } else if (status.status === "uploading") {
        resuming = true;
      } else if (status.status === "initiating") {
        clearSavedUpload(jobId);
        session = null;
        sessionId = null;
        resuming = false;
      } else {
        clearSavedUpload(jobId);
        session = null;
        sessionId = null;
        throw unavailableSessionError(status.errorMessage);
      }
    }

    const initiated = session
      ? { sessionId: session.sessionId, partSizeBytes: session.partSize }
      : await initiateMotionAmplificationMultipartUpload(jobId, fingerprint);
    sessionId = initiated.sessionId;

    if (!session) {
      session = {
        jobId,
        sessionId: initiated.sessionId,
        partSize: initiated.partSizeBytes,
        fileSize: file.size,
        fingerprint,
      };
    }
    if (fileHandle) {
      await saveMultipartFileHandle(jobId, fileHandle).catch(() => undefined);
    }

    if (controller.signal.aborted) throw cancelledError();

    const currentSession = session;

    const partSize = currentSession.partSize;
    const partCount = Math.ceil(file.size / partSize);
    const parts: Array<{ partNumber: number; eTag: string }> = new Array(
      partCount,
    );
    const loaded = new Map<number, number>();
    let nextPartNumber = 1;
    let completedBytes = 0;

    if (resuming) {
      const { parts: remoteParts } =
        await listMotionAmplificationMultipartParts(
          jobId,
          currentSession.sessionId,
        );
      const remote = new Map(
        remoteParts.map((part) => [part.partNumber, part]),
      );
      for (const actual of remote.values()) {
        const partNumber = actual.partNumber;
        const expectedSize = Math.min(
          partSize,
          file.size - (partNumber - 1) * partSize,
        );
        if (
          partNumber < 1 ||
          partNumber > partCount ||
          actual.size !== expectedSize
        )
          continue;
        const eTag = actual.eTag;
        parts[partNumber - 1] = { partNumber, eTag };
        completedBytes += expectedSize;
      }
    }

    function reportProgress() {
      const inFlightBytes = Array.from(loaded.values()).reduce(
        (total, bytes) => total + bytes,
        0,
      );
      onProgress(
        Math.min(
          99,
          Math.floor(((completedBytes + inFlightBytes) / file.size) * 100),
        ),
      );
    }

    reportProgress();

    async function uploadPart(partNumber: number): Promise<void> {
      const start = (partNumber - 1) * partSize;
      const part = file.slice(start, Math.min(start + partSize, file.size));
      for (let attempt = 0; attempt < MAX_PART_ATTEMPTS; attempt++) {
        if (controller.signal.aborted) throw cancelledError();
        const startedAt = Date.now();
        let phase: "part-url" | "s3-put" = "part-url";
        loaded.set(partNumber, 0);
        try {
          const { uploadUrl } = await presignMotionAmplificationPart(
            jobId,
            currentSession.sessionId,
            partNumber,
          );
          if (controller.signal.aborted) throw cancelledError();
          if (!uploadStartedNotified) {
            uploadStartedNotified = true;
            onUploadStarted?.();
          }
          phase = "s3-put";
          const eTag = await uploadMotionAmplificationPart(
            uploadUrl,
            part,
            (percent) => {
              loaded.set(partNumber, Math.floor((part.size * percent) / 100));
              reportProgress();
            },
            { signal: controller.signal, timeoutMs: PART_TIMEOUT_MS },
          );
          loaded.delete(partNumber);
          completedBytes += part.size;
          parts[partNumber - 1] = { partNumber, eTag };
          reportProgress();
          return;
        } catch (error) {
          loaded.delete(partNumber);
          reportProgress();
          if (controller.signal.aborted) throw cancelledError();
          console.warn("motionAmplification.multipart.partAttemptFailed", {
            jobId,
            partNumber,
            attempt: attempt + 1,
            maxAttempts: MAX_PART_ATTEMPTS,
            phase,
            durationMs: Date.now() - startedAt,
            willRetry:
              isRetryableUploadError(error) && attempt < MAX_PART_ATTEMPTS - 1,
            errorName: error instanceof Error ? error.name : "UnknownError",
            errorMessage:
              error instanceof S3UploadError ? error.message : undefined,
            failureKind:
              error instanceof S3UploadError ? error.kind : undefined,
            httpStatus:
              error instanceof S3UploadError ? error.httpStatus : undefined,
            s3Code: error instanceof S3UploadError ? error.s3Code : undefined,
            s3RequestId:
              error instanceof S3UploadError ? error.s3RequestId : undefined,
          });
          if (!isRetryableUploadError(error)) throw error;
          if (attempt === MAX_PART_ATTEMPTS - 1) {
            throw new MultipartUploadPausedError(jobId, currentSession);
          }
          await waitBeforeRetry(attempt, controller.signal);
        }
      }
    }

    async function worker(): Promise<void> {
      while (nextPartNumber <= partCount) {
        const partNumber = nextPartNumber++;
        if (parts[partNumber - 1]) continue;
        await uploadPart(partNumber);
      }
    }

    workers = Array.from(
      { length: Math.min(MAX_CONCURRENT_PARTS, partCount) },
      worker,
    );
    await Promise.all(workers);
    if (controller.signal.aborted) throw cancelledError();
    await completeMotionAmplificationMultipartUpload(
      jobId,
      currentSession.sessionId,
      parts,
    );
    completed = true;
    clearSavedUpload(jobId);
    onProgress(100);
  } catch (error) {
    controller.abort();
    await Promise.allSettled(workers);
    const paused =
      Boolean(sessionId && session) &&
      (error instanceof MultipartUploadPausedError ||
        (signal.aborted && signal.reason === "navigation") ||
        isRetryableUploadError(error));
    if (sessionId && !completed && !paused) {
      await abortMotionAmplificationMultipartUpload(jobId, sessionId).catch(
        () => undefined,
      );
      clearSavedUpload(jobId);
    }
    throw paused
      ? new MultipartUploadPausedError(jobId, session ?? undefined)
      : error;
  } finally {
    signal.removeEventListener("abort", cancel);
  }
}
